"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getEntryDescription = exports.getChangeDescription = exports.getProductFieldName = exports.formatData = void 0;
const lodash_1 = __importDefault(require("lodash"));
const product_1 = require("@treez/commons/sharedTypings/product");
const PRICE_TIER_ID = 'priceTierId';
/**
 * Formats the value into human readable text, also resolving a Price Tier Id to its respective tier label
 * when necessary.
 *
 * @param value value to be formated
 * @param property qualified path of the property
 * @param relatedEntities map of entities containing any entity referred by the property
 * @returns human readable form of the value, or the entity's name if it's a reference
 */
exports.formatData = (value, property, relatedEntities) => {
    var _a;
    if (property === null || property === void 0 ? void 0 : property.endsWith(product_1.ProductField.UOM)) {
        const uom = (_a = lodash_1.default.findKey(product_1.UOM, _value => _value === value)) !== null && _a !== void 0 ? _a : value;
        return lodash_1.default.get(product_1.UOMDisplayName, uom, value);
    }
    if (property === null || property === void 0 ? void 0 : property.endsWith(PRICE_TIER_ID)) {
        // intentionally fails if price tier can't be found
        const { priceTiers } = relatedEntities;
        const tierLabel = lodash_1.default.find(priceTiers, { id: value }).label;
        return `'${tierLabel}'`;
    }
    if (lodash_1.default.isNumber(value)) {
        return value.toString();
    }
    // picks on "42" and "42.5555" - but not "42.", ".42", "a42" or "42b"
    if (lodash_1.default.isString(value) && /^\d+(\.\d+)?$/.test(value)) {
        return Number.parseFloat(value).toString();
    }
    let text = value;
    if (Array.isArray(value)) {
        text = value.join(', ');
    }
    return `'${text}'`;
};
/**
 * Resolves the human readable property name as mapped in `@treez/commons` constants or,
 * when unable to resolve the mapping, separates and capitalizes words.
 *
 * Transformation examples:
 *
 * | Property               | Field Name        |
 * | ---------------------- | ----------------- |
 * | `details.cdbMg`        | Milligrams CBD    |
 * | `custom.priceTierId`   | Price Tier Id     |
 * | `unmmapedProperty`     | Unmapped Property |
 *
 * @param path qualified property path
 * @returns a human readable name for the property
 *
 * @see {@link _.startCase}
 */
exports.getProductFieldName = (path) => {
    const [property, type] = path.split('.').reverse();
    let fieldsToProp;
    if (type === product_1.ProductField.ATTRIBUTES) {
        fieldsToProp = product_1.ProductAttributesField;
    }
    else if (type === product_1.ProductField.DETAILS) {
        fieldsToProp = product_1.ProductDetailsField;
    }
    else {
        fieldsToProp = product_1.ProductField;
    }
    const field = lodash_1.default.findKey(fieldsToProp, prop => prop === property);
    let fieldName = lodash_1.default.get(product_1.ProductAttributesDisplayName, field // undefined is handled but lodash doesn't have an interface for that
    );
    if (!fieldName) {
        fieldName = lodash_1.default.startCase(property);
    }
    return fieldName;
};
const getImageChangeDescription = (contextualizer) => {
    const suffix = contextualizer
        ? ` ${contextualizer}`
        : '';
    return `Images changed${suffix}`;
};
const getStatusChangeDescription = (newStatus, contextualizer) => {
    let action = newStatus.toLowerCase();
    if (newStatus === product_1.CatalogProductStatus.ACTIVE) {
        action = 'activated';
    }
    else if (newStatus === product_1.CatalogProductStatus.DEACTIVATED) {
        action = 'deactivated';
    }
    else if (newStatus === product_1.CatalogProductStatus.DELETED) {
        action = 'deleted';
    }
    return `Product ${action}` + (contextualizer
        ? ` ${contextualizer}`
        : '');
};
/**
 * Generates a human readable description of a change, indicating what changed, where, and from which value to what.
 *
 * @param property the qualified path of the property that changed
 * @param newValue raw new value
 * @param oldValue raw old value
 * @param options  extended options and data
 */
exports.getChangeDescription = (property, newValue, oldValue, options) => {
    let contextualizer = '';
    if (property.startsWith('catalogOverrides.')) {
        contextualizer = 'in catalog';
    }
    if (property.endsWith('images')) {
        return getImageChangeDescription(contextualizer);
    }
    if (property.endsWith('status')) {
        return getStatusChangeDescription(newValue, contextualizer);
    }
    let fieldName;
    if (property.endsWith('descriptions.main')) {
        fieldName = product_1.ProductAttributesDisplayName.DESCRIPTIONS;
    }
    else if (property.endsWith(PRICE_TIER_ID)) {
        fieldName = 'Price Tier';
    }
    else {
        fieldName = exports.getProductFieldName(property);
    }
    const contextualizedField = fieldName + (contextualizer
        ? ` ${contextualizer}`
        : '');
    if (lodash_1.default.isBoolean(newValue || oldValue)) {
        return `${newValue ? 'Checked' : 'Unchecked'} ${contextualizedField}`;
    }
    const formatter = (options === null || options === void 0 ? void 0 : options.valueFormatter) || exports.formatData;
    const oldValueStr = oldValue ? formatter(oldValue, property, options === null || options === void 0 ? void 0 : options.relatedEntities) : null;
    const newValueStr = newValue ? formatter(newValue, property, options === null || options === void 0 ? void 0 : options.relatedEntities) : null;
    if (!lodash_1.default.isNil(newValue) && !lodash_1.default.isNil(oldValue)) {
        return `${contextualizedField} changed from ${oldValueStr} to ${newValueStr}`;
    }
    if (lodash_1.default.isNil(newValue)) {
        return `${contextualizedField} removed`;
    }
    return `${contextualizedField} set to ${newValueStr}`;
};
/**
 * Generates a human friendly description of a history entry.
 * @param entry history entry to be described
 * @param valueFormatter custom function to format values in the description
 */
exports.getEntryDescription = (entry, valueFormatter) => {
    if (entry.property) {
        return exports.getChangeDescription(entry.property, entry.newValue, entry.oldValue, {
            relatedEntities: entry.relatedEntities,
            valueFormatter,
        });
    }
    return lodash_1.default.get(product_1.ActionTypeDisplayName, entry.actionType);
};
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiaGlzdG9yeS5qcyIsInNvdXJjZVJvb3QiOiIiLCJzb3VyY2VzIjpbIi4uLy4uL3NyYy9saWIvaGlzdG9yeS50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiOzs7Ozs7QUFBQSxvREFBc0Q7QUFLdEQsa0VBVW9GO0FBR3BGLE1BQU0sYUFBYSxHQUF5QixhQUFhLENBQUM7QUFpQjFEOzs7Ozs7OztHQVFHO0FBQ1UsUUFBQSxVQUFVLEdBQW1CLENBQUMsS0FBVSxFQUFFLFFBQWlCLEVBQUUsZUFBd0MsRUFBRSxFQUFFOztJQUNsSCxJQUFJLFFBQVEsYUFBUixRQUFRLHVCQUFSLFFBQVEsQ0FBRSxRQUFRLENBQUMsc0JBQVksQ0FBQyxHQUFHLEdBQUc7UUFDdEMsTUFBTSxHQUFHLFNBQUcsZ0JBQUMsQ0FBQyxPQUFPLENBQUMsYUFBRyxFQUFFLE1BQU0sQ0FBQyxFQUFFLENBQUMsTUFBTSxLQUFLLEtBQUssQ0FBQyxtQ0FBSSxLQUFLLENBQUM7UUFDaEUsT0FBTyxnQkFBQyxDQUFDLEdBQUcsQ0FBQyx3QkFBYyxFQUFFLEdBQUcsRUFBRSxLQUFLLENBQUMsQ0FBQztLQUM1QztJQUVELElBQUksUUFBUSxhQUFSLFFBQVEsdUJBQVIsUUFBUSxDQUFFLFFBQVEsQ0FBQyxhQUFhLEdBQUc7UUFDbkMsbURBQW1EO1FBQ25ELE1BQU0sRUFBQyxVQUFVLEVBQUMsR0FBRyxlQUFnQixDQUFDO1FBQ3RDLE1BQU0sU0FBUyxHQUFHLGdCQUFDLENBQUMsSUFBSSxDQUFDLFVBQVUsRUFBRSxFQUFDLEVBQUUsRUFBRSxLQUFLLEVBQUMsQ0FBRSxDQUFDLEtBQUssQ0FBQztRQUN6RCxPQUFPLElBQUksU0FBUyxHQUFHLENBQUM7S0FDM0I7SUFFRCxJQUFJLGdCQUFDLENBQUMsUUFBUSxDQUFDLEtBQUssQ0FBQyxFQUFFO1FBQ25CLE9BQU8sS0FBSyxDQUFDLFFBQVEsRUFBRSxDQUFDO0tBQzNCO0lBQ0QscUVBQXFFO0lBQ3JFLElBQUksZ0JBQUMsQ0FBQyxRQUFRLENBQUMsS0FBSyxDQUFDLElBQUksZUFBZSxDQUFDLElBQUksQ0FBQyxLQUFLLENBQUMsRUFBRTtRQUNsRCxPQUFPLE1BQU0sQ0FBQyxVQUFVLENBQUMsS0FBSyxDQUFDLENBQUMsUUFBUSxFQUFFLENBQUM7S0FDOUM7SUFFRCxJQUFJLElBQUksR0FBRyxLQUFLLENBQUM7SUFDakIsSUFBSSxLQUFLLENBQUMsT0FBTyxDQUFDLEtBQUssQ0FBQyxFQUFFO1FBQ3RCLElBQUksR0FBRyxLQUFLLENBQUMsSUFBSSxDQUFDLElBQUksQ0FBQyxDQUFDO0tBQzNCO0lBQ0QsT0FBTyxJQUFJLElBQUksR0FBRyxDQUFDO0FBQ3ZCLENBQUMsQ0FBQztBQUVGOzs7Ozs7Ozs7Ozs7Ozs7O0dBZ0JHO0FBQ1UsUUFBQSxtQkFBbUIsR0FBRyxDQUFDLElBQVksRUFBVSxFQUFFO0lBQ3hELE1BQU0sQ0FBQyxRQUFRLEVBQUUsSUFBSSxDQUFDLEdBQUcsSUFBSSxDQUFDLEtBQUssQ0FBQyxHQUFHLENBQUMsQ0FBQyxPQUFPLEVBQUUsQ0FBQztJQUNuRCxJQUFJLFlBQW9DLENBQUM7SUFDekMsSUFBSSxJQUFJLEtBQUssc0JBQVksQ0FBQyxVQUFVLEVBQUU7UUFDbEMsWUFBWSxHQUFHLGdDQUFzQixDQUFDO0tBQ3pDO1NBQ0ksSUFBSSxJQUFJLEtBQUssc0JBQVksQ0FBQyxPQUFPLEVBQUU7UUFDcEMsWUFBWSxHQUFHLDZCQUFtQixDQUFDO0tBQ3RDO1NBQ0k7UUFDRCxZQUFZLEdBQUcsc0JBQVksQ0FBQztLQUMvQjtJQUVELE1BQU0sS0FBSyxHQUFHLGdCQUFDLENBQUMsT0FBTyxDQUFDLFlBQVksRUFBRSxJQUFJLENBQUMsRUFBRSxDQUFDLElBQUksS0FBSyxRQUFRLENBQUMsQ0FBQztJQUNqRSxJQUFJLFNBQVMsR0FBdUIsZ0JBQUMsQ0FBQyxHQUFHLENBQ3JDLHNDQUE0QixFQUM1QixLQUFlLENBQUMscUVBQXFFO0tBQ3hGLENBQUM7SUFDRixJQUFJLENBQUMsU0FBUyxFQUFFO1FBQ1osU0FBUyxHQUFHLGdCQUFDLENBQUMsU0FBUyxDQUFDLFFBQVEsQ0FBQyxDQUFDO0tBQ3JDO0lBQ0QsT0FBTyxTQUFTLENBQUM7QUFDckIsQ0FBQyxDQUFDO0FBRUYsTUFBTSx5QkFBeUIsR0FBRyxDQUFDLGNBQXdCLEVBQVUsRUFBRTtJQUNuRSxNQUFNLE1BQU0sR0FBRyxjQUFjO1FBQ3pCLENBQUMsQ0FBQyxJQUFJLGNBQWMsRUFBRTtRQUN0QixDQUFDLENBQUMsRUFBRSxDQUFDO0lBQ1QsT0FBTyxpQkFBaUIsTUFBTSxFQUFFLENBQUM7QUFDckMsQ0FBQyxDQUFDO0FBRUYsTUFBTSwwQkFBMEIsR0FBRyxDQUFDLFNBQWlCLEVBQUUsY0FBdUIsRUFBRSxFQUFFO0lBQzlFLElBQUksTUFBTSxHQUFHLFNBQVMsQ0FBQyxXQUFXLEVBQUUsQ0FBQztJQUNyQyxJQUFJLFNBQVMsS0FBSyw4QkFBb0IsQ0FBQyxNQUFNLEVBQUU7UUFDM0MsTUFBTSxHQUFHLFdBQVcsQ0FBQztLQUN4QjtTQUFNLElBQUksU0FBUyxLQUFLLDhCQUFvQixDQUFDLFdBQVcsRUFBRTtRQUN2RCxNQUFNLEdBQUcsYUFBYSxDQUFDO0tBQzFCO1NBQU0sSUFBSSxTQUFTLEtBQUssOEJBQW9CLENBQUMsT0FBTyxFQUFFO1FBQ25ELE1BQU0sR0FBRyxTQUFTLENBQUM7S0FDdEI7SUFDRCxPQUFPLFdBQVcsTUFBTSxFQUFFLEdBQUcsQ0FBQyxjQUFjO1FBQ3hDLENBQUMsQ0FBQyxJQUFJLGNBQWMsRUFBRTtRQUN0QixDQUFDLENBQUMsRUFBRSxDQUFDLENBQUM7QUFDZCxDQUFDLENBQUM7QUFVRjs7Ozs7OztHQU9HO0FBQ1UsUUFBQSxvQkFBb0IsR0FBRyxDQUNoQyxRQUF3QixFQUN4QixRQUFxQixFQUNyQixRQUFxQixFQUNyQixPQUE2QyxFQUN2QyxFQUFFO0lBQ1IsSUFBSSxjQUFjLEdBQUcsRUFBRSxDQUFDO0lBQ3hCLElBQUksUUFBUSxDQUFDLFVBQVUsQ0FBQyxtQkFBbUIsQ0FBQyxFQUFFO1FBQzFDLGNBQWMsR0FBRyxZQUFZLENBQUM7S0FDakM7SUFFRCxJQUFJLFFBQVEsQ0FBQyxRQUFRLENBQUMsUUFBUSxDQUFDLEVBQUU7UUFDN0IsT0FBTyx5QkFBeUIsQ0FBQyxjQUFjLENBQUMsQ0FBQztLQUNwRDtJQUNELElBQUksUUFBUSxDQUFDLFFBQVEsQ0FBQyxRQUFRLENBQUMsRUFBRTtRQUM3QixPQUFPLDBCQUEwQixDQUFDLFFBQVEsRUFBRSxjQUFjLENBQUMsQ0FBQTtLQUM5RDtJQUVELElBQUksU0FBaUIsQ0FBQztJQUN0QixJQUFJLFFBQVEsQ0FBQyxRQUFRLENBQUMsbUJBQW1CLENBQUMsRUFBRTtRQUN4QyxTQUFTLEdBQUcsc0NBQTRCLENBQUMsWUFBWSxDQUFDO0tBQ3pEO1NBQ0ksSUFBSSxRQUFRLENBQUMsUUFBUSxDQUFDLGFBQWEsQ0FBQyxFQUFFO1FBQ3ZDLFNBQVMsR0FBRyxZQUFZLENBQUM7S0FDNUI7U0FDSTtRQUNELFNBQVMsR0FBRywyQkFBbUIsQ0FBQyxRQUFRLENBQUMsQ0FBQztLQUM3QztJQUVELE1BQU0sbUJBQW1CLEdBQUcsU0FBUyxHQUFHLENBQUMsY0FBYztRQUNuRCxDQUFDLENBQUMsSUFBSSxjQUFjLEVBQUU7UUFDdEIsQ0FBQyxDQUFDLEVBQUUsQ0FBQyxDQUFDO0lBRVYsSUFBSSxnQkFBQyxDQUFDLFNBQVMsQ0FBQyxRQUFRLElBQUksUUFBUSxDQUFDLEVBQUU7UUFDbkMsT0FBTyxHQUFHLFFBQVEsQ0FBQyxDQUFDLENBQUMsU0FBUyxDQUFDLENBQUMsQ0FBQyxXQUFXLElBQUksbUJBQW1CLEVBQUUsQ0FBQztLQUN6RTtJQUVELE1BQU0sU0FBUyxHQUFHLENBQUEsT0FBTyxhQUFQLE9BQU8sdUJBQVAsT0FBTyxDQUFFLGNBQWMsS0FBSSxrQkFBVSxDQUFDO0lBRXhELE1BQU0sV0FBVyxHQUFHLFFBQVEsQ0FBQyxDQUFDLENBQUMsU0FBUyxDQUFDLFFBQVEsRUFBRSxRQUFRLEVBQUUsT0FBTyxhQUFQLE9BQU8sdUJBQVAsT0FBTyxDQUFFLGVBQWUsQ0FBQyxDQUFDLENBQUMsQ0FBQyxJQUFJLENBQUM7SUFDOUYsTUFBTSxXQUFXLEdBQUcsUUFBUSxDQUFDLENBQUMsQ0FBQyxTQUFTLENBQUMsUUFBUSxFQUFFLFFBQVEsRUFBRSxPQUFPLGFBQVAsT0FBTyx1QkFBUCxPQUFPLENBQUUsZUFBZSxDQUFDLENBQUMsQ0FBQyxDQUFDLElBQUksQ0FBQztJQUM5RixJQUFJLENBQUMsZ0JBQUMsQ0FBQyxLQUFLLENBQUMsUUFBUSxDQUFDLElBQUksQ0FBQyxnQkFBQyxDQUFDLEtBQUssQ0FBQyxRQUFRLENBQUMsRUFBRTtRQUMxQyxPQUFPLEdBQUcsbUJBQW1CLGlCQUFpQixXQUFXLE9BQU8sV0FBVyxFQUFFLENBQUM7S0FDakY7SUFDRCxJQUFJLGdCQUFDLENBQUMsS0FBSyxDQUFDLFFBQVEsQ0FBQyxFQUFFO1FBQ25CLE9BQU8sR0FBRyxtQkFBbUIsVUFBVSxDQUFDO0tBQzNDO0lBQ0QsT0FBTyxHQUFHLG1CQUFtQixXQUFXLFdBQVcsRUFBRSxDQUFDO0FBQzFELENBQUMsQ0FBQztBQUdGOzs7O0dBSUc7QUFDVSxRQUFBLG1CQUFtQixHQUFHLENBQUMsS0FBMkMsRUFBRSxjQUErQixFQUFFLEVBQUU7SUFDaEgsSUFBSSxLQUFLLENBQUMsUUFBUSxFQUFFO1FBQ2hCLE9BQU8sNEJBQW9CLENBQUMsS0FBSyxDQUFDLFFBQVEsRUFBRSxLQUFLLENBQUMsUUFBUSxFQUFFLEtBQUssQ0FBQyxRQUFRLEVBQUU7WUFDeEUsZUFBZSxFQUFFLEtBQUssQ0FBQyxlQUFlO1lBQ3RDLGNBQWM7U0FDakIsQ0FBQyxDQUFDO0tBQ047SUFDRCxPQUFPLGdCQUFDLENBQUMsR0FBRyxDQUFDLCtCQUFxQixFQUFFLEtBQUssQ0FBQyxVQUFVLENBQUMsQ0FBQztBQUMxRCxDQUFDLENBQUMifQ==